import React, { useState, useEffect } from 'react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { useTranslation } from 'react-i18next';
import { useRecoilValue, useRecoilState } from 'recoil';
import { useNavigate, useParams } from 'react-router-dom';
import { useLocalStorage } from 'react-use';
import { userAtom, notificationsAtom } from '../../../atoms/Atoms';
import { H2, H4 } from '../../shared-components/typography/Title';
import AuthLayout from '../../shared-components/auth-layout/AuthLayout';
import Button from '../../shared-components/buttons/Button';
import Form from '../../shared-components/form/Form';
import Input from '../../shared-components/form/Input';
import api from '../../../api/api';
import envConfig from '../../../envConfig';
import { encryptString } from '../../../functions';

function UpdatePass() {
    const [storageUser] = useLocalStorage(`${envConfig.StoragePrefix}-user`);
    const [notifications, setNotifications] = useRecoilState(notificationsAtom);
    const { t } = useTranslation();
    const params = useParams();
    const navigate = useNavigate();
    const user = useRecoilValue(userAtom);
    const [loading, setLoading] = useState(false);

    if (!storageUser) {
      window.location = '/';
    }

    useEffect(() => {
      if (user) {
        if (!user?.forcePasswordReset) {
            window.location = '/home';
        }
      }
    }, [user]);

    function submit(values) {
      setLoading(true);
      api.post('auth/password/update', {
        CurrentPassword: encryptString(values.currentpassword),
        NewPassword: encryptString(values.password),
      })
      .then((response) => {
        setLoading(false);
        if (response.data.success) {
          setNotifications([
            ...notifications,
            {
              title: t('yay'),
              description: response.data.message,
              error: false,
            },
          ]);
          navigate('/');
        } else {
          setNotifications([
            ...notifications,
            {
              title: t('error'),
              description: response.data.error,
              error: true,
            },
          ]);
        }
      })
      .catch((error) => {
        setLoading(false);
        setNotifications([
          ...notifications,
          {
            title: t('error'),
            description: error.message,
            error: true,
          },
        ]);
      });
    }

    const passwordFormik = useFormik({
      initialValues: {
        currentpassword: '',
        password: '',
        passwordConfirmation: ''
      },
      validationSchema: Yup.object().shape({
        currentpassword: Yup.string().required(t('required_field')),
        password: Yup.string().required(t('required_field')),
        passwordConfirmation: Yup.string()
        .oneOf(
          [Yup.ref('password'), null],
          t('same_password')
        )
        .required(t('required_field')),
      }),
      onSubmit: (values, { setSubmitting }) => submit(values),
    });

    return (
      <AuthLayout title={`${t('welcome_message')} - ${envConfig.SiteTitle}`}>
          <H2 className="text-center" text={t('enter_your_new_password_below')} />
          <Form className="mt-6 w-full space-y-7">
            <Input
              label={t('current_password')}
              required
              id="currentpassword"
              name="currentpassword"
              type="password"
              className=""
              autoComplete="password"
              value={passwordFormik.values.currentpassword}
              onChange={passwordFormik.handleChange}
              error={
                passwordFormik.errors.password &&
                passwordFormik.touched.password &&
                passwordFormik.errors.password
              }
            />
            <Input
              label={t('new_password')}
              required
              id="password"
              name="password"
              type="password"
              className=""
              value={passwordFormik.values.password}
              onChange={passwordFormik.handleChange}
              error={
                passwordFormik.errors.password &&
                passwordFormik.touched.password &&
                passwordFormik.errors.password
              }
            />
            <Input
              label={t('confirm_password')}
              required
              id="passwordConfirmation"
              name="passwordConfirmation"
              type="password"
              value={passwordFormik.values.passwordConfirmation}
              onChange={passwordFormik.handleChange}
              error={
                passwordFormik.errors.passwordConfirmation &&
                passwordFormik.touched.passwordConfirmation &&
                passwordFormik.errors.passwordConfirmation
              }
            />
          </Form>
          <Button
            className="bg-primaryColor mt-8 mb-3 w-full"
            textColor="text-primaryTextColor"
            loaderColor="bg-primaryTextColor"
            borderColor="border-primaryBorderColor"
            loading={loading}
            text={t('submit')}
            onClick={passwordFormik.submitForm}
          />
      </AuthLayout>
    );
}

export default UpdatePass;
